"use client";

import defaultAvatar from "@/assets/global/error-image.svg";
import { cn } from "@/lib/utils";
import Image, { type StaticImageData } from "next/image";
import { type ComponentProps, memo, type ReactNode, useEffect, useMemo, useState } from "react";
import { type ImageUrlOpts, processImageUrl } from "./smart-image-utils";

export { processImageUrl } from "./smart-image-utils";

/**
 * 统一图片组件属性。
 *
 * 设计目标：
 * 1. 兼容 next/image 的大部分原生能力
 * 2. 对字符串 src 自动补 CDN / OSS 处理
 * 3. 提供 fallback 与加载失败兜底
 * 4. 尽量不破坏现有调用方式
 */
interface SmartImageProps extends Omit<ComponentProps<typeof Image>, "src"> {
    /**
     * 图片地址。
     * 支持：
     * - 远程图片 URL
     * - 相对 OSS 路径
     * - next/image 静态导入对象
     */
    src?: string | StaticImageData;
    /**
     * 回退内容。
     * 支持：
     * - string: 作为备用图片地址
     * - StaticImageData: 作为备用图片资源
     * - ReactNode: 直接渲染自定义兜底内容
     */
    fallback?: string | StaticImageData | ReactNode;
    /**
     * 默认 CDN 前缀，优先级高于环境变量。
     */
    cdnPrefix?: string;
    /**
     * yesorno 资源专用 CDN 前缀。
     */
    ynCdnPrefix?: string;
    /**
     * 图片 URL 处理选项。
     * 仅影响字符串类型的 src，不影响静态导入图片对象。
     */
    imageUrlOptions?: Omit<ImageUrlOpts, "cdnPrefix" | "ynCdnPrefix">;
}

/**
 * Pick Tailwind object-* classes from a className string.
 */
const pickObjectClasses = ( className?: string ) => {
    if ( !className ) return "";
    return className
        .split( /\s+/ )
        .filter( ( item ) => item.startsWith( "object-" ) )
        .join( " " );
};

/**
 * 判断是否为静态导入的图片对象
 */
const isStaticImageData = ( src: any ): src is StaticImageData => {
    return src && typeof src === "object" && "src" in src;
};

/**
 * 判断是否为字符串类型的 fallback
 */
const isStringFallback = ( fallback: any ): fallback is string => {
    return typeof fallback === "string";
};

/**
 * 判断是否为 StaticImageData 类型的 fallback
 */
const isStaticImageDataFallback = ( fallback: any ): fallback is StaticImageData => {
    return isStaticImageData( fallback );
};

/**
 * 判断是否为 ReactNode 类型的 fallback
 */
const isReactNodeFallback = ( fallback: any ): fallback is ReactNode => {
    return !isStringFallback( fallback ) && !isStaticImageDataFallback( fallback );
};

function SmartImageComponent( {
    src,
    fallback,
    cdnPrefix,
    ynCdnPrefix,
    imageUrlOptions,
    alt = "",
    onError,
    ...restProps
}: SmartImageProps ) {
    const [ hasError, setHasError ] = useState( false );

    /**
     * 汇总 URL 处理配置。
     *
     * 这里会把组件级别传入的 cdnPrefix / ynCdnPrefix 与 imageUrlOptions 合并。
     *
     * 注意：
     * - 当前只有显式传入 imageUrlOptions 时，才会触发 OSS 缩放参数生成
     * - 组件本身的 width / height 只负责 next/image 布局，不再自动参与压缩参数推导
     */
    const resolvedImageUrlOptions = useMemo<ImageUrlOpts>( () => ( {
        ...imageUrlOptions,
        cdnPrefix,
        ynCdnPrefix,
    } ), [ cdnPrefix, ynCdnPrefix, imageUrlOptions ] );

    /**
     * 计算最终渲染源。
     *
     * 分支顺序：
     * 1. src 为空时优先看 fallback
     * 2. 静态导入图片直接透传
     * 3. 字符串 src 统一走 processImageUrl
     * 4. ReactNode fallback 交由组件直接渲染
     */
    const { finalSrc, shouldShowFallbackContent } = useMemo( () => {
        // 如果 src 为空（空字符串、null、undefined）
        if ( !src || ( typeof src === "string" && !src.trim() ) ) {
            // 检查 fallback
            if ( fallback ) {
                // 如果 fallback 是 string 或 StaticImageData，直接作为 src 使用
                if ( isStringFallback( fallback ) || isStaticImageDataFallback( fallback ) ) {
                    return { finalSrc: fallback, shouldShowFallbackContent: false };
                }
                // 如果 fallback 是 ReactNode，标记需要显示 fallback 内容
                return { finalSrc: null, shouldShowFallbackContent: true };
            }
            // 没有 fallback，使用默认头像
            return { finalSrc: defaultAvatar, shouldShowFallbackContent: false };
        }

        // src 不为空，按正常流程处理
        // 如果是静态导入的图片对象，直接使用
        if ( isStaticImageData( src ) ) {
            return { finalSrc: src, shouldShowFallbackContent: false };
        }

        // 如果是字符串，处理路径
        if ( typeof src === "string" ) {
            return { finalSrc: processImageUrl( src, resolvedImageUrlOptions ), shouldShowFallbackContent: false };
        }

        return { finalSrc: src, shouldShowFallbackContent: false };
    }, [ src, fallback, resolvedImageUrlOptions ] );

    /**
     * 当图片源变化时，重置错误状态。
     * 否则从 A 图失败切到 B 图时，组件仍会错误地停留在失败兜底状态。
     */
    useEffect( () => {
        setHasError( false );
    }, [ finalSrc ] );

    /**
     * 图片加载失败处理。
     *
     * 行为：
     * 1. 记录错误状态，触发默认兜底分支
     * 2. 继续调用外部 onError，避免吞掉调用方逻辑
     */
    const handleError = ( event: React.SyntheticEvent<HTMLImageElement, Event> ) => {
        setHasError( true );
        if ( onError ) {
            onError( event );
        }
    };

    /**
     * fallback 是 ReactNode 时，直接渲染自定义内容，不再走图片组件。
     */
    if ( shouldShowFallbackContent && fallback && isReactNodeFallback( fallback ) ) {
        return <>{fallback}</>;
    }

    /**
     * 没有可用图片源时，使用默认占位图。
     */
    if ( !finalSrc ) {
        return <Image src={defaultAvatar} alt={alt} {...restProps} />;
    }

    /**
     * 图片加载失败时，如果 fallback 不是 ReactNode，则退回默认占位图。
     */
    if ( hasError && !( fallback && isReactNodeFallback( fallback ) ) ) {
        return <Image src={defaultAvatar} alt={alt} {...restProps} onError={handleError} />;
    }

    /**
     * 对“外部字符串图片地址 + 未显式提供 width/height/fill”的场景做自动 fill。
     *
     * 这是为了兼容项目里大量只写 className 尺寸、没有传 next/image 宽高的旧用法。
     * 组件会自动包一层相对定位容器，并使用 fill 渲染，避免这些页面在升级后失效。
     */
    if (
        typeof finalSrc === "string"
        && restProps.width === undefined
        && restProps.height === undefined
        && restProps.fill !== true
    ) {
        const { className, style, sizes, ...imageProps } = restProps;

        return (
            <span className={cn( "relative block overflow-hidden w-full h-full", className )} style={style}>
                <Image
                    src={finalSrc}
                    alt={alt}
                    fill
                    sizes={sizes || "100vw"}
                    className={pickObjectClasses( className ) || "object-cover"}
                    onError={handleError}
                    {...imageProps}
                />
            </span>
        );
    }

    /**
     * 常规分支：
     * - 静态导入图片
     * - 已提供 width / height / fill 的图片
     * - 不需要自动 fill 包裹的情况
     */
    return (
        <Image
            src={finalSrc}
            alt={alt}
            onError={handleError}
            {...restProps}
        />
    );
}

// 使用 React.memo 包装组件，避免父组件 state 更新导致的无意义重渲染
// 当 props 未变化时，组件不会重新渲染，从而避免图片重复加载
export const SmartImage = memo( SmartImageComponent );
